import { safeExternalUrl } from '../../../utils/safeExternalUrl';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Table, Spin, message, Avatar, Modal, Form, InputNumber, Input, Select, Switch, Tooltip } from 'antd';
import { ArrowLeftOutlined, DollarOutlined, EditOutlined, PlusOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table {
    background: transparent !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr > td {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: ${COLORS.hover} !important;
  }
`;

const CurrencyDetails: React.FC = () => {
  const { currencyId } = useParams<{ currencyId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(true);
  const [currencyData, setCurrencyData] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [contractModalVisible, setContractModalVisible] = useState(false);
  const [contractForm] = Form.useForm();
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    fetchCurrencyDetails();
    fetchBlockchainNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId]);

  const fetchCurrencyDetails = async () => {
    if (!currencyId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(
        (API_ENDPOINTS.ADMIN_GET_CURRENCY || '/api/Administrator/currency/{id}').replace('{id}', currencyId)
      );
      
      if (response.data.status === 'success') {
        setCurrencyData(response.data.data);
      } else {
        message.error('Failed to load currency details');
      }
    } catch (error: any) {
      console.error('Error fetching currency details:', error);
      message.error(error.response?.data?.message || 'Failed to load currency details');
    } finally {
      setLoading(false);
    }
  };

  const handleResyncToWatcher = async () => {
    if (!currencyId) return;
    
    try {
      setResyncing(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_RESYNC_CURRENCY_TO_WATCHER.replace('{id}', currencyId));
      
      if (response.data?.status === 'success') {
        message.success('Currency synced to OnchainWatcher successfully');
        fetchCurrencyDetails();
      } else {
        message.error(response.data?.message || 'Failed to sync to watcher');
      }
    } catch (error: any) {
      console.error('Error resyncing currency to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync to watcher');
    } finally {
      setResyncing(false);
    }
  };

  const fetchBlockchainNetworks = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS);
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setBlockchainNetworks(items);
      }
    } catch (error: any) {
      console.error('Error fetching blockchain networks:', error);
    }
  };

  const handleEdit = () => {
    if (!currencyData) return;
    
    form.setFieldsValue({
      name: currencyData.name,
      symbol: currencyData.symbol,
      decimalPrecision: currencyData.decimalPrecision,
      iconUrl: currencyData.iconUrl,
      aggregatorSlug: currencyData.aggregatorSlug,
      oneUsdValue: currencyData.oneUsdValue,
      minSweepAmountInUsd: currencyData.minSweepAmountInUsd,
      status: currencyData.status,
      currencyStatus: currencyData.currencyStatus,
      allowInOnchainWatcherMonitor: currencyData.allowInOnchainWatcherMonitor ?? true,
      supportsRPCPolling: currencyData.supportsRPCPolling ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Ensure numeric fields are sent as numbers to match API expectations
      const payload: any = {
        ...values,
        decimalPrecision:
          values.decimalPrecision !== undefined && values.decimalPrecision !== null
            ? Number(values.decimalPrecision)
            : values.decimalPrecision,
        oneUsdValue:
          values.oneUsdValue !== undefined && values.oneUsdValue !== null
            ? Number(values.oneUsdValue)
            : values.oneUsdValue,
        minSweepAmountInUsd:
          values.minSweepAmountInUsd !== undefined && values.minSweepAmountInUsd !== null
            ? Number(values.minSweepAmountInUsd)
            : values.minSweepAmountInUsd,
      };

      await apiClient.put(
        (API_ENDPOINTS.ADMIN_UPDATE_CURRENCY || '/api/Administrator/currency/update/{id}').replace('{id}', currencyId!),
        payload
      );
      message.success('Asset updated successfully');
      setModalVisible(false);
      fetchCurrencyDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update currency');
    }
  };

  const handleAddContract = () => {
    if (!currencyId) return;
    contractForm.resetFields();
    contractForm.setFieldsValue({
      currencyId: currencyId,
      isNative: false,
      status: 'ACTIVE',
    });
    setContractModalVisible(true);
  };

  const handleContractSubmit = async () => {
    try {
      const values = await contractForm.validateFields();
      const payload: any = {
        address: values.address,
        currencyId: values.currencyId,
        blockchainNetworkId: values.blockchainNetworkId,
        isNative: values.isNative || false,
      };
      if (values.status) {
        payload.status = values.status;
      }
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_CONTRACT_ADDRESS, payload);
      message.success('Contract address created successfully');
      setContractModalVisible(false);
      fetchCurrencyDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create contract address');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
      DELISTED: { color: '#6B7280', text: 'Delisted' },
      PARTIAL: { color: '#F59E0B', text: 'Partial (pending approval)' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const contractAddressColumns = [
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (address: string, record: any) => {
        if (!address) return '-';
        const shortAddress = `${address.substring(0, 10)}...${address.substring(address.length - 8)}`;
        const explorerUrl = record.blockchainNetwork?.explorerUrl;
        if (explorerUrl) {
          // Construct explorer URL - most explorers use /address/ or /token/ pattern
          const explorerLink = `${explorerUrl.replace(/\/$/, '')}/address/${address}`;
          return (
            <a href={explorerLink} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
              {shortAddress}
            </a>
          );
        }
        return shortAddress;
      },
    },
    {
      title: 'Blockchain Network',
      key: 'blockchainNetwork',
      render: (_: any, record: any) => {
        const network = record.blockchainNetwork;
        if (!network) return '-';
        return (
          <Space>
            {network.iconUrl && (
              <Avatar
                src={network.iconUrl}
                size="small"
                style={{ width: 20, height: 20 }}
              />
            )}
            <span>{network.name || network.abbreviation || '-'}</span>
            {network.abbreviation && network.name && (
              <span style={{ color: COLORS.textSecondary }}>({network.abbreviation})</span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Is Native',
      dataIndex: 'isNative',
      key: 'isNative',
      render: (isNative: boolean) => isNative ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!currencyData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Asset Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kivu/currencies')}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Asset Details
          </Title>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResyncToWatcher}
            loading={resyncing}
            disabled={resyncing}
          >
            Resync to Watcher
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit Asset
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <Avatar
              src={currencyData.iconUrl}
              icon={<DollarOutlined />}
              size={64}
              style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
            />
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {currencyData.name} ({currencyData.symbol})
              </div>
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Asset ID">{currencyData.id}</Descriptions.Item>
          <Descriptions.Item label="Watcher ID">
            {currencyData.watcherId ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{currencyData.watcherId}</span>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not synced</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Symbol">{currencyData.symbol}</Descriptions.Item>
          <Descriptions.Item label="Name">{currencyData.name}</Descriptions.Item>
          <Descriptions.Item label="Decimal Precision">{currencyData.decimalPrecision}</Descriptions.Item>
          <Descriptions.Item label="CoinMarketCap Slug">
            {currencyData.aggregatorSlug ? (
              <Tag color="blue">{currencyData.aggregatorSlug}</Tag>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not set (manual pricing)</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Value per 1 USD">
            {currencyData.oneUsdValue ? `${Number(currencyData.oneUsdValue).toFixed(8)} ${currencyData.symbol}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Exchange Rate (USD)">
            {(() => {
              const oneUsdValue = Number(currencyData.oneUsdValue);
              if (!oneUsdValue || oneUsdValue === 0 || isNaN(oneUsdValue)) return '-';
              const rate = 1 / oneUsdValue;
              // Format based on value magnitude
              if (rate >= 1000) {
                return `$${rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              } else if (rate >= 1) {
                return `$${rate.toFixed(2)}`;
              } else if (rate >= 0.01) {
                return `$${rate.toFixed(4)}`;
              } else {
                return `$${rate.toFixed(8)}`;
              }
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="Min Sweep Amount">
            {currencyData.minSweepAmount ? Number(currencyData.minSweepAmount).toFixed(8) : '0'}
          </Descriptions.Item>
          <Descriptions.Item label="Min Sweep Amount (USD)">
            {currencyData.minSweepAmountInUsd ? `$${Number(currencyData.minSweepAmountInUsd).toFixed(2)}` : '$0.00'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(currencyData.status)}</Descriptions.Item>
          <Descriptions.Item label="Asset Status">{getStatusTag(currencyData.currencyStatus)}</Descriptions.Item>
          {currencyData.iconUrl && (
            <Descriptions.Item label="Icon URL">
              <a href={safeExternalUrl(currencyData.iconUrl)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {currencyData.iconUrl.split('/').pop() || currencyData.iconUrl}
              </a>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created At">
            {currencyData.createdAt ? new Date(currencyData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {currencyData.updatedAt ? new Date(currencyData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <StyledCard 
        title="Contract Addresses"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddContract}
            size="small"
          >
            Add Contract
          </Button>
        }
      >
        {currencyData.contractAddresses && currencyData.contractAddresses.length > 0 ? (
          <Table
            columns={contractAddressColumns}
            dataSource={currencyData.contractAddresses}
            rowKey="id"
            pagination={false}
            style={{ background: 'transparent' }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textSecondary }}>
            No contract addresses found. Click "Add Contract" to create one.
          </div>
        )}
      </StyledCard>

      <Modal
        title="Edit Asset"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Update"
        cancelText="Cancel"
        width={600}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="symbol"
            label="Symbol"
            rules={[{ required: true, message: 'Please enter symbol' }]}
          >
            <Input placeholder="e.g., BTC, ETH" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., Bitcoin, Ethereum" />
          </Form.Item>
          <Form.Item
            name="decimalPrecision"
            label="Decimal Precision"
            rules={[{ required: true, message: 'Please enter decimal precision' }]}
          >
            <InputNumber min={0} max={18} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="iconUrl"
            label="Icon URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="aggregatorSlug"
            label={
              <span>
                CoinMarketCap Slug{' '}
                <Tooltip title="The CoinMarketCap slug used to fetch live prices (e.g., 'bitcoin', 'ethereum', 'tether'). Use 'assetChainPricing' for custom pricing logic.">
                  <InfoCircleOutlined style={{ color: '#22D3EE', cursor: 'pointer' }} />
                </Tooltip>
              </span>
            }
          >
            <Input placeholder="e.g., bitcoin, ethereum, tether" />
          </Form.Item>
          <Form.Item
            name="oneUsdValue"
            label="Value per 1 USD (e.g., 0.000025 means 0.000025 units = 1 USD)"
            rules={[{ required: true, message: 'Please enter value per 1 USD' }]}
          >
            <InputNumber min={0} step={0.00000001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="minSweepAmountInUsd"
            label="Min Sweep Amount (USD)"
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="currencyStatus"
            label="Asset Status"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="DELISTED">Delisted</Select.Option>
              <Select.Option value="PARTIAL">Partial (pending approval)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Contract Address"
        open={contractModalVisible}
        onOk={handleContractSubmit}
        onCancel={() => setContractModalVisible(false)}
        okText="Create"
        cancelText="Cancel"
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={contractForm}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="address"
            label="Contract Address"
            rules={[{ required: true, message: 'Please enter contract address' }]}
          >
            <Input placeholder="0x..." />
          </Form.Item>
          <Form.Item
            name="currencyId"
            label="Asset"
            rules={[{ required: true, message: 'Please select currency' }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="blockchainNetworkId"
            label="Blockchain Network"
            rules={[{ required: true, message: 'Please select blockchain network' }]}
          >
            <Select
              placeholder="Select Blockchain Network"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={blockchainNetworks.map(network => ({
                value: network.id,
                label: `${network.name} (${network.abbreviation})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="isNative"
            label="Is Native Asset"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default CurrencyDetails;

